import { Link } from 'react-router-dom';

/**
 * Waitlist signup section component
 * Displays the beta tutor signup form
 * @param {Object} props - Component props  
 * @param {number} props.spotsRemaining - Number of spots remaining
 * @param {Function} props.onOpenModal - Callback to open the waitlist modal
 * @param {boolean} props.submitting - Whether form is submitting
 * @param {string} props.formStatus - Form status message
 */
function WaitlistSection({ spotsRemaining, onOpenModal, submitting, formStatus }) {
  const isAtCapacity = spotsRemaining <= 0;

  return (
    <section id="waitlist" className="bg-gradient-to-br from-slate-50 to-white">
      <div className="mx-auto max-w-7xl mobile-full-width px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="max-w-lg mx-auto">
          <div className="rounded-3xl border-2 border-indigo-200 bg-white p-6 sm:p-10 shadow-modern-lg text-center">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mb-3">Live Beta Access</h2>
            
            {/* Free signup badge */}
            <div className="mb-6 rounded-2xl bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 p-4">
              <p className="text-2xl sm:text-3xl font-extrabold text-green-700 mb-1">
                100% Free to Sign Up
              </p>
              <p className="text-sm text-slate-600">
                Get immediate access—no payment, contracts, or hoops.
              </p>
            </div>
            
            {/* Spots remaining indicator */}
            <div className="mb-8">
              <p className="text-base font-bold text-transparent bg-gradient-to-r from-orange-500 to-red-500 bg-clip-text mb-2">
                First 20 tutors get Wurlo free for life
              </p>
              <p className="text-sm font-semibold text-slate-600">
                {isAtCapacity ? (
                  'All 20 free founder spots have been claimed — stay tuned for the next wave.'
                ) : (
                  <>
                    Secure one of the 20 lifetime-free founder spots. Only {' '}
                    <span className="spots-remaining text-orange-600 font-bold">{spotsRemaining}</span>{' '}
                    free spots remain.
                  </>
                )}
              </p>
            </div>
            
            {/* Benefits list */}
            <div className="space-y-4 mb-8 text-left">
              <div className="flex items-start gap-3">
                <svg className="w-6 h-6 text-indigo-600 flex-shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span className="text-base text-slate-700">Once-in-a-lifetime discount on lifetime access post-beta</span>
              </div>
              <div className="flex items-start gap-3">
                <svg className="w-6 h-6 text-indigo-600 flex-shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span className="text-base text-slate-700">Direct access to founder for feedback</span>
              </div>
              <div className="flex items-start gap-3">
                <svg className="w-6 h-6 text-indigo-600 flex-shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span className="text-base text-slate-700">Priority access to all beta features</span>
              </div>
              <div className="flex items-start gap-3">
                <svg className="w-6 h-6 text-indigo-600 flex-shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span className="text-base text-slate-700">We’ll redirect you straight to beta.wurlo.org the moment you join</span>
              </div>
            </div>
            
            {/* CTA and status */}
            <div className="w-full space-y-4">
              <button
                onClick={onOpenModal}
                className="cta-primary btn-press w-full inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 px-8 py-4 text-white text-lg font-bold hover:from-indigo-700 hover:to-purple-700 focus-ring shadow-lg whitespace-nowrap disabled:cursor-not-allowed disabled:opacity-70"
                disabled={submitting || isAtCapacity}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M12 2l3 7h7l-5.5 4 2 7L12 17l-6.5 4 2-7L2 10h7l3-7z"/>
                </svg>
                {isAtCapacity ? 'Free users are full' : 'Become a Beta Tutor'}
              </button>

              {isAtCapacity && (
                <p className="text-sm font-semibold text-center text-red-600">
                  Our 20 free beta seats are closed. Join the waitlist to be notified when more open.
                </p>
              )}

              {/* Free badge */}
              <div className="rounded-lg bg-green-50 border border-green-200 px-3 py-2">
                <p className="text-sm font-bold text-green-700 text-center">
                  ✓ 100% Free — No Payment Required
                </p>
              </div>

              {/* Terms */}
              <p className="text-xs text-center text-slate-600">
                By clicking above, you agree to our <Link to="/terms" className="text-indigo-600 hover:text-indigo-700 underline">Terms &amp; Conditions</Link> and <Link to="/privacy" className="text-indigo-600 hover:text-indigo-700 underline">Privacy Policy</Link>
              </p>

              {/* Form status message */}
              {formStatus && (
                <div className={`rounded-xl border-2 px-4 py-3 ${formStatus.includes('Success') || formStatus.includes('🎉') ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                  <p className={`text-sm text-center font-semibold ${formStatus.includes('Success') || formStatus.includes('🎉') ? 'text-green-700' : 'text-red-600'}`} role="status" aria-live="polite">{formStatus}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default WaitlistSection;
